// app/proposals/new/page.jsx
// Standalone "Create Proposal" page using Fluent UI v8 (plain JS)
// - Collects core proposal fields
// - Lets you link People, Projects, and Tags before saving
// - Expects REST endpoints (adjust as needed):
//   POST   /api/proposals                         -> create proposal, returns { id, ... }
//   GET    /api/people?q=                         -> search people
//   POST   /api/proposals/:id/people              -> { person_id }
//   GET    /api/projects?q=                       -> search projects
//   POST   /api/proposals/:id/projects            -> { project_id }
//   GET    /api/tags?q=                           -> search tags
//   POST   /api/proposals/:id/tags                -> { tag_id }
//
// If your existing people/project linking endpoints differ, update the POSTs near handleSubmit.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Text,
  TextField,
  Dropdown,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  DatePicker,
  Separator,
  Label,
  Spinner,
  SearchBox,
} from '@fluentui/react';
import { mergeStyleSets, mergeStyles } from '@fluentui/react';

// ---------- Generic Chip Picker (search + selected chips) ----------
function LinkChipsPicker({ label, searchUrl, selectedItems, setSelectedItems, idKey = 'id', nameKey = 'name', placeholder = 'Search…' }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const styles = useMemo(() => mergeStyleSets({
    box: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 12 },
    chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    chip: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      borderRadius: 9999, fontSize: 13, lineHeight: '28px', height: 28,
      padding: '0 10px', border: '1px solid var(--neutralTertiaryAlt,#c8c8c8)',
      background: 'transparent', cursor: 'pointer', userSelect: 'none',
    },
    chipSelected: { background: 'var(--themePrimary,#0078d4)', color: 'white', borderColor: 'var(--themePrimary,#0078d4)' },
    addIcon: { fontWeight: 700 },
  }), []);

  // Fetch options on query change (debounced)
  useEffect(() => {
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${searchUrl}${query ? `?q=${encodeURIComponent(query)}` : ''}`, { signal: ctrl.signal });
        const data = await res.json();
        setOptions(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [query, searchUrl]);

  const selectedIds = useMemo(() => new Set(selectedItems.map(it => it[idKey])), [selectedItems, idKey]);
  const available = useMemo(() => options.filter(o => !selectedIds.has(o[idKey])), [options, selectedIds, idKey]);

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
        <Label>{label}</Label>
        {loading && <Spinner size={0} label="Searching" labelPosition="left" />}
      </Stack>
      <div className={styles.box}>
        <Stack tokens={{ childrenGap: 8 }}>
          <SearchBox placeholder={placeholder} value={query} onChange={(_, v) => setQuery(v || '')} />
          {/* Selected */}
          <Label>Selected</Label>
          <div className={styles.chipRow}>
            {selectedItems.length === 0 && <Text variant="small" styles={{ root: { opacity: .7 }}}>None</Text>}
            {selectedItems.map(item => (
              <button key={item[idKey]} type="button" className={mergeStyles(styles.chip, styles.chipSelected)} onClick={() => setSelectedItems(prev => prev.filter(p => p[idKey] !== item[idKey]))}>
                <span>{item[nameKey] || item.label || item.title}</span>
                <span aria-hidden>×</span>
              </button>
            ))}
          </div>
          <Separator />
          {/* Available */}
          <Label>Available</Label>
          <div className={styles.chipRow}>
            {available.length === 0 && <Text variant="small" styles={{ root: { opacity: .7 }}}>No results</Text>}
            {available.map(item => (
              <button key={item[idKey]} type="button" className={styles.chip} onClick={() => setSelectedItems(prev => [...prev, item])}>
                <span>{item[nameKey] || item.label || item.title}</span>
                <span className={styles.addIcon} aria-hidden>+</span>
              </button>
            ))}
          </div>
        </Stack>
      </div>
    </Stack>
  );
}

// ---------- Create Proposal Page ----------
export default function NewProposalPage(props) {
  // Form state — adjust keys to match your `proposals` table exactly
  const [form, setForm] = useState({
    title: '',
    client_id: '',
    proposal_number: '',
    proposal_type: "RFP",
    status: 'Draft',
    submission_deadline: null, // date
    value_amount: '',
    currency: 'CAD',
    summary: '',
    notes: '',
    about: "",
    approach: "",
    workplan: "",
    budget: "",
    submitted: "",
    was_Successful: false
  });

  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Linked entities (selected arrays)
  const [people, setPeople] = useState([]);   // [{id, name, ...}]
  const [projects, setProjects] = useState([]); // [{id, title, ...}]
  const [tags, setTags] = useState([]);         // [{id, name}]

  // Load clients for dropdown (optional)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/clients');
        const data = await res.json();
        setClients(Array.isArray(data.data) ? data.data : []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const statusOptions = [
    { key: 'Draft', text: 'Draft' },
    { key: 'In Review', text: 'In Review' },
    { key: 'Submitted', text: 'Submitted' },
    { key: 'Won', text: 'Won' },
    { key: 'Lost', text: 'Lost' },
  ];

  const clientOptions = clients.map(c => ({ key: c.id, text: c.name || c.client_name || c.title }));

  const styles = useMemo(() => mergeStyleSets({
    page: { maxWidth: 1100, margin: '0 auto', padding: 16 },
    card: { border: '1px solid var(--neutralLight,#e1e1e1)', borderRadius: 12, padding: 16 },
  }), []);

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      // 1) Create the proposal
      const res = await fetch('/api/proposals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to create proposal');
      const created = await res.json();
      const proposalId = created.id;

      // 2) Link People
      for (const p of people) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`/api/proposals/${proposalId}/people`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ person_id: p.id }),
        });
        if (!r.ok) throw new Error('Failed to link person');
      }

      // 3) Link Projects
      for (const pr of projects) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`/api/proposals/${proposalId}/projects`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: pr.id }),
        });
        if (!r.ok) throw new Error('Failed to link project');
      }

      // 4) Link Tags
      for (const t of tags) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(`/api/proposals/${proposalId}/tags`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tag_id: t.id }),
        });
        if (!r.ok) throw new Error('Failed to link tag');
      }

      setSuccess('Proposal created successfully.');
      // TODO: router.push(`/proposals/${proposalId}`)
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to create proposal');
    } finally {
      setSaving(false);
    }
  };

  const halfRow = (left, right, key) => (
    <Stack key={key} horizontal tokens={{ childrenGap: 12 }}>
      <Stack.Item grow disableShrink>{left}</Stack.Item>
      <Stack.Item grow disableShrink>{right}</Stack.Item>
    </Stack>
  );

  return (
    <div className={styles.page}>
      <Text variant="xLarge">New Proposal</Text>
      <Text variant="small" styles={{ root: { opacity: .7 } }}>Create a new proposal and link people, projects, and tags.</Text>

      <Separator />

      {/* Alerts */}
      {error && (<MessageBar messageBarType={MessageBarType.error} isMultiline>{error}</MessageBar>)}
      {success && (<MessageBar messageBarType={MessageBarType.success} isMultiline>{success}</MessageBar>)}

      {/* Form Card */}
      <div className={styles.card}>
        <Stack tokens={{ childrenGap: 12 }}>
          {halfRow(
            <TextField label="Title" required value={form.title} onChange={(_, v) => setField('title', v || '')} />, 
            <Dropdown label="Client" options={clientOptions} selectedKey={form.client_id} onChange={(_, o) => setField('client_id', o ? o.key : '')} />,
            'row1'
          )}

          {halfRow(
            <TextField label="Proposal #" value={form.proposal_number} onChange={(_, v) => setField('proposal_number', v || '')} />, 
            <Dropdown label="Status" options={statusOptions} selectedKey={form.status} onChange={(_, o) => setField('status', o ? o.key : 'Draft')} />, 
            'row2'
          )}

          {halfRow(
            <DatePicker label="Submission deadline" value={form.submission_deadline} onSelectDate={(d) => setField('submission_deadline', d)} placeholder="Select a date" />, 
            <Dropdown label="Currency" options={[{key:'CAD',text:'CAD'},{key:'USD',text:'USD'}]} selectedKey={form.currency} onChange={(_,o)=>setField('currency', o?o.key:'CAD')} />,
            'row3'
          )}

          {halfRow(
            <TextField label="Estimated value" value={form.value_amount} onChange={(_, v) => setField('value_amount', v || '')} />, 
            <TextField label="Notes" value={form.notes} onChange={(_, v) => setField('notes', v || '')} />, 
            'row4'
          )}

          <TextField label="Summary" multiline autoAdjustHeight value={form.summary} onChange={(_, v) => setField('summary', v || '')} />
        </Stack>
      </div>

      <Separator />

      {/* Linkers */}
      <Stack tokens={{ childrenGap: 16 }}>
        <LinkChipsPicker label="People"   searchUrl="/api/people"   selectedItems={people}   setSelectedItems={setPeople}   nameKey="name" />
        <LinkChipsPicker label="Projects" searchUrl="/api/projects" selectedItems={projects} setSelectedItems={setProjects} nameKey="title" />
        <LinkChipsPicker label="Tags"     searchUrl="/api/tags"     selectedItems={tags}     setSelectedItems={setTags}     nameKey="name" />
      </Stack>

      <Separator />

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <PrimaryButton text={saving ? 'Saving…' : 'Create Proposal'} onClick={handleSubmit} disabled={saving || !form.title} />
        <DefaultButton text="Cancel" onClick={() => props.history.push('/proposals')} />
      </Stack>
    </div>
  );
}
